import { useState, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { Wifi, WifiOff, QrCode, RefreshCw, RotateCcw } from "lucide-react";
import { useSocket } from "../hooks/useSocket";
import { useToast } from "../components/Toast";
import { fmtDateTime } from "../utils/helpers";
import api from "../utils/api";
import Card from "../components/Card";
import StatusBadge from "../components/StatusBadge";

type SignalSeverity = "ok" | "warn" | "critical";

type ParsedDashboardEvent = {
  id: number;
  ts: string;
  level: "info" | "warn" | "error" | "debug";
  event: string;
  payload_json?: string;
  payload: Record<string, unknown>;
};

function parseEventPayload(payloadJson?: string): Record<string, unknown> {
  if (!payloadJson) return {};
  try {
    const parsed = JSON.parse(payloadJson);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function getPayloadString(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  return typeof value === "string" && value ? value : null;
}

function getPayloadNumber(payload: Record<string, unknown>, key: string): number | null {
  const value = payload[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export default function DashboardPage() {
  const { status, events, readiness, connected: sockConnesso } = useSocket();
  const { toast } = useToast();
  const [qrSrc, setQrSrc] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [events]);

  useEffect(() => {
    if (status?.status === "qr_required" && !qrSrc) fetchQr();
  }, [status?.status]);

  const fetchQr = async () => {
    if (qrLoading) return;
    setQrLoading(true);
    try {
      const res = await api.get("/wa/qr");
      if (res.data.ok && res.data.qr) setQrSrc(res.data.qr);
    } catch (err: any) {
      if (err.response?.status === 429) toast("Rate limited. Wait before refreshing.", "error");
    } finally {
      setQrLoading(false);
    }
  };

  const reconnectWhatsApp = async () => {
    try {
      setQrSrc(null);
      const res = await api.post("/wa/worker/reset");
      if (!res.data.ok) {
        toast(res.data.error || "Errore riconnessione", "error");
        return;
      }
      toast("Riconnessione avviata. Attendi il nuovo QR...", "success");
      let attempts = 0;
      const poll = window.setInterval(async () => {
        attempts += 1;
        try {
          const qrRes = await api.get("/wa/qr");
          if (qrRes.data.ok && qrRes.data.qr) {
            setQrSrc(qrRes.data.qr);
            window.clearInterval(poll);
          }
        } catch {
          if (attempts >= 20) window.clearInterval(poll);
        }
      }, 1500);
    } catch {
      toast("Errore riconnessione", "error");
    }
  };

  const levelColor: Record<string, string> = {
    info: "text-blue-400",
    warn: "text-amber-400",
    error: "text-red-400",
    debug: "text-zinc-500",
  };

  const signalTone: Record<SignalSeverity, { card: string; badge: string }> = {
    ok: { card: "border-emerald-500/20 bg-emerald-500/5", badge: "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20" },
    warn: { card: "border-amber-500/20 bg-amber-500/5", badge: "bg-amber-500/10 text-amber-200 border border-amber-500/20" },
    critical: { card: "border-red-500/20 bg-red-500/5", badge: "bg-red-500/10 text-red-200 border border-red-500/20" },
  };

  const readinessReasonLabels: Record<string, string> = {
    db_unavailable: "DB unavailable",
    session_state_missing: "Session state missing",
    worker_disconnected: "Worker disconnected",
    worker_qr_required: "QR required",
    worker_auto_paused: "Worker auto-paused",
    queue_failures_spiking: "Queue failures spiking",
  };

  const waStatus = status?.status || "disconnected";

  const parsedEvents = useMemo<ParsedDashboardEvent[]>(() => {
    return events.map((event) => ({ ...event, payload: parseEventPayload(event.payload_json) }));
  }, [events]);

  const readinessTone: SignalSeverity = !readiness
    ? "warn"
    : readiness.ok
      ? "ok"
      : readiness.reasons.includes("db_unavailable") || readiness.reasons.includes("worker_auto_paused")
        ? "critical"
        : "warn";

  const readinessSummary = readiness
    ? readiness.ok
      ? "Ready: DB, worker, and queue are healthy."
      : readiness.reasons.map((reason) => readinessReasonLabels[reason] || reason).join(", ")
    : "Waiting for readiness signal...";

  const operatorSignals = useMemo(() => {
    const reversed = [...parsedEvents].reverse();
    const findEvent = (matcher: (event: ParsedDashboardEvent) => boolean) => reversed.find(matcher) ?? null;
    const latestWorkerEvent = findEvent((event) => ["worker_auto_paused", "worker_manual_auto_pause", "worker_exit", "worker_paused"].includes(event.event));
    const latestAuthEvent = findEvent((event) => ["wa_auth_failure", "wa_reconnect_loop_detected"].includes(event.event));
    const latestQueueEvent = findEvent((event) => event.event === "campaign_queue_failure_spike" || (event.event === "worker_auto_paused" && getPayloadString(event.payload, "reason") === "consecutive_failures"));
    const latestManualPauseEvent = findEvent((event) => event.event === "worker_manual_auto_pause");
    const authEventCount = parsedEvents.filter((event) => ["wa_auth_failure", "wa_reconnect_loop_detected"].includes(event.event)).length;
    const queueFailures = latestQueueEvent ? getPayloadNumber(latestQueueEvent.payload, "consecutive_failures") : null;
    const reconnectAttempt = latestAuthEvent ? getPayloadNumber(latestAuthEvent.payload, "attempt") : null;
    const authReason = latestAuthEvent ? getPayloadString(latestAuthEvent.payload, "reason") : null;
    const manualReason = latestManualPauseEvent ? getPayloadString(latestManualPauseEvent.payload, "reason") : null;

    return [
      { key: "worker", label: "Worker", severity: waStatus === "connected" ? "ok" : waStatus === "connecting" ? "warn" : "critical", summary: waStatus === "connected" ? "Worker connesso e in salute." : latestWorkerEvent ? `Ultimo evento: ${latestWorkerEvent.event.replace(/_/g, " ")}.` : `Stato attuale: ${waStatus.replace(/_/g, " ")}.`, updatedAt: latestWorkerEvent?.ts ?? status?.updated_at ?? null },
      { key: "auth", label: "Auth / sessione", severity: latestAuthEvent ? "critical" : "ok", summary: latestAuthEvent ? latestAuthEvent.event === "wa_reconnect_loop_detected" ? `Reconnect loop rilevato (tentativo ${reconnectAttempt ?? "?"}).` : `Ultimo failure auth/sessione: ${authReason ?? latestAuthEvent.event}.` : "Nessun failure auth/sessione recente nel feed live.", updatedAt: latestAuthEvent?.ts ?? null, meta: latestAuthEvent ? `${authEventCount} eventi recenti` : "clean" },
      { key: "queue", label: "Queue", severity: latestQueueEvent ? "critical" : "ok", summary: latestQueueEvent ? latestQueueEvent.event === "campaign_queue_failure_spike" ? `${queueFailures ?? "Più"} errori consecutivi in coda rilevati.` : "La queue ha auto-messo in pausa il worker." : "Nessun picco recente di errori in coda.", updatedAt: latestQueueEvent?.ts ?? null },
      { key: "manual-pause", label: "Pause manuale", severity: latestManualPauseEvent ? "warn" : "ok", summary: latestManualPauseEvent ? `Latch auto-pause attivo (${manualReason ?? "manual"}).` : "Nessun latch manuale di auto-pause rilevato.", updatedAt: latestManualPauseEvent?.ts ?? null },
    ] as Array<{ key: string; label: string; severity: SignalSeverity; summary: string; updatedAt: string | null; meta?: string }>;
  }, [parsedEvents, status?.updated_at, waStatus]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-zinc-400 text-sm mt-1">Connessione WhatsApp e monitoraggio</p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={sockConnesso ? "connected" : "disconnected"} size="md" />
          <StatusBadge status={waStatus} size="md" />
          <span className={`px-3 py-1 rounded-full text-xs uppercase tracking-wide ${signalTone[readinessTone].badge}`}>
            {readiness?.state || "degraded"}
          </span>
        </div>
      </div>

      <Card title="Platform Readiness" badge={<span className="text-xs text-zinc-500">/ready + worker + queue</span>}>
        <div className={`rounded-xl border p-4 ${signalTone[readinessTone].card}`}>
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-white">Operational readiness</h3>
            <span className={`px-2 py-1 rounded-full text-[11px] uppercase tracking-wide ${signalTone[readinessTone].badge}`}>
              {readiness?.state || "degraded"}
            </span>
          </div>
          <p className="mt-3 text-sm text-zinc-200 leading-relaxed">{readinessSummary}</p>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-zinc-500">
            <span>worker: {readiness?.worker.state || "unknown"}</span>
            <span>queue failures: {readiness?.queue.consecutive_failures ?? 0}</span>
            <span>campaign backlog: {readiness?.queue.pending_campaign_jobs ?? 0}</span>
            <span>manual backlog: {readiness?.queue.pending_manual_sends ?? 0}</span>
            <span>{fmtDateTime(readiness?.checked_at || null)}</span>
          </div>
        </div>
      </Card>

      <Card title="Operator Signals" badge={<span className="text-xs text-zinc-500">top alerts</span>}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {operatorSignals.map((signal) => (
            <div key={signal.key} className={`rounded-xl border p-4 ${signalTone[signal.severity].card}`}>
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-white">{signal.label}</h3>
                <span className={`px-2 py-1 rounded-full text-[11px] uppercase tracking-wide ${signalTone[signal.severity].badge}`}>{signal.severity}</span>
              </div>
              <p className="mt-3 text-sm text-zinc-200 leading-relaxed">{signal.summary}</p>
              <div className="mt-3 flex items-center justify-between gap-3 text-[11px] text-zinc-500">
                <span>{signal.meta || "live"}</span>
                <span>{fmtDateTime(signal.updatedAt)}</span>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card title="WhatsApp Connection" actions={
          <button onClick={reconnectWhatsApp} className="text-xs text-zinc-400 hover:text-fuchsia-400 flex items-center gap-1 transition-colors">
            <RotateCcw size={14} /> Riconnetti WhatsApp
          </button>
        }>
          <div className="flex flex-col items-center justify-center py-4">
            {waStatus === "connected" && (
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-center">
                <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                  <Wifi size={32} className="text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold text-emerald-400">Connesso</h3>
                <p className="text-sm text-zinc-500 mt-1">{fmtDateTime(status?.updated_at)}</p>
              </motion.div>
            )}
            {waStatus === "qr_required" && (
              <div className="text-center">
                <p className="text-sm text-zinc-400 mb-3">Scansiona il QR con WhatsApp per riconnettere il dispositivo</p>
                {qrSrc ? (
                  <img src={qrSrc} alt="QR" className="mx-auto rounded-xl border border-zinc-700 p-2 bg-white max-w-[200px]" />
                ) : (
                  <div className="w-[200px] h-[200px] mx-auto rounded-xl border border-zinc-700 flex items-center justify-center bg-zinc-800">
                    <QrCode size={48} className="text-zinc-600" />
                  </div>
                )}
                <button onClick={fetchQr} disabled={qrLoading} className="mt-3 px-4 py-2 text-sm rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 disabled:opacity-40 transition-colors flex items-center gap-2 mx-auto">
                  <RefreshCw size={14} className={qrLoading ? "animate-spin" : ""} />
                  {qrLoading ? "Caricamento..." : "Aggiorna QR"}
                </button>
              </div>
            )}
            {(waStatus === "disconnected" || waStatus === "connecting") && (
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
                  <WifiOff size={32} className="text-zinc-500" />
                </div>
                <h3 className="text-lg font-semibold text-zinc-400">{waStatus === "connecting" ? "Connessione in corso..." : "Disconnesso"}</h3>
                <p className="text-sm text-zinc-500 mt-2 mb-4">Se la sessione è scaduta, genera un nuovo QR di riconnessione.</p>
                <button onClick={reconnectWhatsApp} className="px-4 py-2 text-sm rounded-lg bg-fuchsia-600 text-white hover:bg-fuchsia-500 transition-colors flex items-center gap-2 mx-auto">
                  <RotateCcw size={14} /> Genera nuovo QR
                </button>
              </div>
            )}
          </div>
        </Card>

        <div className="lg:col-span-2">
          <Card title="Event Log" badge={<span className="text-xs text-zinc-500">(last 50)</span>} actions={<span className="text-xs text-zinc-500">{events.length} events</span>}>
            <div ref={logRef} className="h-[320px] overflow-y-auto font-mono text-xs space-y-0.5">
              {events.length === 0 ? (
                <div className="text-zinc-600 text-center py-12">Waiting for events...</div>
              ) : (
                events.map((ev, i) => {
                  let payload = "";
                  if (ev.payload_json) {
                    try { payload = JSON.stringify(JSON.parse(ev.payload_json)); } catch { payload = ev.payload_json; }
                  }
                  return (
                    <div key={ev.id || i} className="flex gap-2 py-0.5 border-b border-zinc-800/50">
                      <span className="text-zinc-600 shrink-0">[{fmtDateTime(ev.ts)}]</span>
                      <span className={(levelColor[ev.level] || "text-zinc-400") + " font-bold shrink-0"}>
                        [{ev.level?.toUpperCase()}]
                      </span>
                      <span className="text-zinc-300 font-semibold shrink-0">{ev.event}</span>
                      {payload && <span className="text-zinc-600 truncate">{payload}</span>}
                    </div>
                  );
                })
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
